import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button, TermsAcceptanceHistory } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import type { AdminLawyer } from '../types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type LawyerVerificationStatus = 'Verifierad' | 'Ej Verifierad';
type TabType = 'details' | 'projects';

interface LawyerFormData {
  name: string;
  organizationNumber: string;
  lawyerCompanyName: string;
  email: string;
  phone: string;
  vatNumber: string;
  website: string;
  lawyerPresentation: string;
  verifiedLawyer: LawyerVerificationStatus;
  address: string;
  postalCode: string;
  city: string;
  notes: string;
}

export function AdminLawyerView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [lawyer, setLawyer] = useState<AdminLawyer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<LawyerFormData>({
    name: '',
    organizationNumber: '',
    lawyerCompanyName: '',
    email: '',
    phone: '',
    vatNumber: '',
    website: '',
    lawyerPresentation: '',
    verifiedLawyer: 'Ej Verifierad',
    address: '',
    postalCode: '',
    city: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const lawyers = await adminApi.getAllLawyers();
      const foundLawyer = lawyers.find(l => l.id === id);
      if (foundLawyer) {
        setLawyer(foundLawyer);
        setFormData({
          name: foundLawyer.name || '',
          organizationNumber: foundLawyer.organizationNumber || '',
          lawyerCompanyName: foundLawyer.lawyerCompanyName || '',
          email: foundLawyer.email || '',
          phone: foundLawyer.phone || '',
          vatNumber: foundLawyer.vatNumber || '',
          website: foundLawyer.website || '',
          lawyerPresentation: foundLawyer.lawyerPresentation || '',
          verifiedLawyer: (foundLawyer.verifiedLawyer as LawyerVerificationStatus) || 'Ej Verifierad',
          address: foundLawyer.address || '',
          postalCode: foundLawyer.postalCode || '',
          city: foundLawyer.city || '',
          notes: foundLawyer.notes || ''
        });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const handleLawyerPresentationChange = (value: string) => {
    setFormData(prev => ({ ...prev, lawyerPresentation: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.email');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !lawyer) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const { name: _verifiedName, email: _verifiedEmail, ...details } = formData;
      await adminApi.updateLawyer(lawyer.id, details);
      setMessage({ type: 'success', text: t('lawyers.form.saveSuccess') });
      // Reload data to get updated values
      await loadData();
    } catch (error) {
      console.error('Failed to update lawyer:', error);
      setMessage({ type: 'error', text: t('lawyers.form.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!lawyer) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <h4>{t('lawyerView.notFound')}</h4>
          <Link to="/admin/lawyers" className="btn btn-primary mt-3">
            {t('common.back')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('lawyerView.title')} - {lawyer.lawyerNumber} - {lawyer.name}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminLawyers.title'), path: '/admin/lawyers' },
            { label: t('lawyerView.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('lawyerView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <div className="d-flex gap-2">
            <Link to="/admin/lawyers" className="btn btn-light-brand">
              <i className="feather-arrow-left me-2"></i>
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="nav-tabs-wrapper">
          <ul className="nav nav-tabs nav-tabs-custom-style" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                {t('lawyerView.tabs.details')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'projects' ? 'active' : ''}`}
                onClick={() => setActiveTab('projects')}
              >
                {t('lawyerView.tabs.projects')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Details Tab */}
        <div className={`tab-pane fade ${activeTab === 'details' ? 'show active' : ''}`}>
          <Card>
            <div className="card-header">
              <h5 className="card-title mb-0">{t('lawyerView.tabs.details')}</h5>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">
                  {/* Lawyer Number (read-only) */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('lawyers.columns.lawyerNumber')}</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={lawyer.lawyerNumber}
                      disabled
                    />
                  </div>

                  {/* Verified Status */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('lawyers.columns.verifiedLawyer')}</label>
                    <select
                      name="verifiedLawyer"
                      className="form-select"
                      value={formData.verifiedLawyer}
                      onChange={handleInputChange}
                    >
                      <option value="Ej Verifierad">{t('lawyers.status.notVerified')}</option>
                      <option value="Verifierad">{t('lawyers.status.verified')}</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  {/* Name */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="name"
                      label={t('lawyers.columns.name')}
                      value={formData.name}
                      readOnly
                      helpText={t('auth.bankid.nameLocked')}
                      maxLength={128}
                    />
                  </div>

                  {/* Organization Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="organizationNumber"
                      label={t('lawyers.columns.organizationNumber')}
                      value={formData.organizationNumber}
                      onChange={handleInputChange}
                      maxLength={32}
                    >
                      <TestOrgNumberButton onGenerated={value => { setFormData(previous => withTestOrgNumber(previous, value)); setFormErrors(previous => ({ ...previous, organizationNumber: '' })); }} />
                    </FormInput>
                  </div>
                </div>

                <div className="row">
                  {/* Lawyer Company Name */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="lawyerCompanyName"
                      label={t('lawyers.columns.lawyerCompanyName')}
                      value={formData.lawyerCompanyName}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Phone */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      type="tel"
                      name="phone"
                      label={t('lawyers.columns.phone')}
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={48}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* VAT Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="vatNumber"
                      label={t('lawyers.columns.vatNumber')}
                      value={formData.vatNumber}
                      onChange={handleInputChange}
                      maxLength={32}
                    />
                  </div>

                  {/* Contact Email */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="email"
                      type="email"
                      label={`${t('lawyers.columns.email')} *`}
                      value={formData.email}
                      readOnly
                      error={formErrors.email}
                      maxLength={128}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* Website */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="website"
                      label={t('lawyers.columns.website')}
                      value={formData.website}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                </div>

                <div className="row">
                  {/* Address */}
                  <div className="col-md-4 mb-3">
                    <AddressAutocomplete
                      name="address"
                      onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                      label={t('lawyers.columns.address')}
                      value={formData.address}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="col-md-4 mb-3">
                    <FormInput
                      name="postalCode"
                      label={t('lawyers.columns.postalCode')}
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      maxLength={16}
                    />
                  </div>

                  {/* City */}
                  <div className="col-md-4 mb-3">
                    <FormInput
                      name="city"
                      label={t('lawyers.columns.city')}
                      value={formData.city}
                      onChange={handleInputChange}
                      maxLength={64}
                    />
                  </div>
                </div>

                {/* Company Presentation (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('lawyers.columns.lawyerPresentation')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.lawyerPresentation}
                    onChange={handleLawyerPresentationChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>

                {/* Notes (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('lawyers.columns.notes')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.notes}
                    onChange={handleNotesChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <Button type="submit" isLoading={isSaving}>
                  <i className="feather-save me-2"></i>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Card>
          {lawyer && <TermsAcceptanceHistory userId={lawyer.id} />}
        </div>

        {/* Projects Tab */}
        <div className={`tab-pane fade ${activeTab === 'projects' ? 'show active' : ''}`}>
          <Card>
            <div className="card-header">
              <h5 className="card-title mb-0">{t('lawyerView.tabs.projects')}</h5>
            </div>
            <div className="card-body">
              <p className="text-muted mb-0">{t('lawyerView.noProjects')}</p>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
